/**
 * The area-specific part of a check's page (Quality / <a check>), above the findings table: what the check looks at,
 * then what only that area has. Production readiness: one card per item (why it matters, where, how to fix, Fix with
 * AI). Code stats: languages, services, the largest files, the most complex functions, duplication, TODOs. Privacy:
 * categories, services, datastores (names only, never values). API contract: per service, breaking changes first,
 * endpoints without login. Performance: bundles and route times against the budgets. Accessibility: the pages checked
 * and the issues by impact, with the element, a help link and Fix with AI. Dependencies, vulnerabilities and the SBOM
 * without a lockfile: "Create lockfiles" (the sandbox runs each service's own package manager, no AI). Plain words: the people reading this may not be developers.
 */
import React from 'react';
import PropTypes from 'prop-types';
import { ArrowSquareOut, Package, Play, ShieldCheck, Sparkle } from '@phosphor-icons/react';
import { Banner, Pill, StatusPill } from './QualityCommon';
import {
  CHECK_PURPOSE, accessibilityPrompt, formatCount, plural, readinessItemOf, readinessPrompt, relativeTime,
} from './qualityModel';
import { CodeScanning, Lighthouse } from './QualityToolDetails';
import External from './QualityExternal';
import DataTable from '../table/DataTable';
import './QualityAreaDetails.css';

const BREAKING = ['api-removed', 'api-method-changed', 'api-required-added', 'api-response-removed'];
const IMPACTS = [
  { id: 'critical', label: 'Critical', tone: 'fail' },
  { id: 'serious', label: 'Serious', tone: 'fail' },
  { id: 'moderate', label: 'Moderate', tone: 'warn' },
  { id: 'minor', label: 'Minor', tone: 'info' },
];
const kb = (bytes) => (bytes || bytes === 0 ? `${Math.round(bytes / 1024)} KB` : '–');
const where = (file, line) => `${file}${line ? `:${line}` : ''}`;

function CodeLink({ file, line, onOpenCode }) {
  if (!file) { return <span>–</span>; }
  if (!onOpenCode) { return <span className="quality-mono quality-small">{where(file, line)}</span>; }
  return <button className="quality-link quality-mono quality-small" onClick={() => onOpenCode({ path: file, line: line || undefined })} type="button">{where(file, line)}</button>;
}

CodeLink.propTypes = { file: PropTypes.string, line: PropTypes.number, onOpenCode: PropTypes.func };


const yes = (v) => (v ? 'Yes' : 'No');
const LANGUAGE_COLUMNS = [
  { key: 'language', label: 'Language', value: (l) => l.language, title: (l) => l.language, render: (l) => <>{l.language}{l.other ? <span className="quality-muted quality-small"> (not code)</span> : null}</> },
  { key: 'files', label: 'Files', width: 90, value: (l) => l.files },
  { key: 'code', label: 'Lines of code', width: 130, value: (l) => l.code || 0, render: (l) => formatCount(l.code || 0) },
  { key: 'share', label: 'Share', width: 90, value: (l) => l.share, render: (l) => (l.share === null || l.share === undefined ? '–' : `${l.share}%`) },
];
const SERVICE_STATS_COLUMNS = [
  { key: 'service', label: 'Service', className: 'quality-mono', width: 200, value: (s) => s.service },
  { key: 'files', label: 'Files', width: 90, value: (s) => s.files },
  { key: 'code', label: 'Lines of code', width: 130, value: (s) => s.code || 0, render: (s) => formatCount(s.code || 0) },
  { key: 'languages', label: 'Languages', value: (s) => Object.keys(s.languages || {}).join(', ') || '–' },
];
const PRIVACY_COLUMNS = [
  { key: 'service', label: 'Service', className: 'quality-mono', value: (s) => s.service },
  { key: 'fields', label: 'Fields', width: 90, value: (s) => s.fields || 0 },
  { key: 'logs', label: 'Written to logs', width: 140, value: (s) => s.logs || 0, render: (s) => (s.logs ? <Pill className="is-xs" tone="warn">{s.logs}</Pill> : 0) },
  { key: 'canDelete', label: 'Can delete it?', width: 130, value: (s) => yes(s.canDelete) },
  { key: 'retention', label: 'Retention rule?', width: 140, value: (s) => yes(s.retention) },
];
const storeStatus = (s) => (s.status === 'done' ? 'Checked' : s.status === 'not_running' ? 'Not running' : 'Could not check');
const STORE_COLUMNS = [
  { key: 'store', label: 'Datastore', width: 200, value: (s) => s.store },
  { key: 'status', label: 'Status', width: 150, value: storeStatus, render: (s) => <Pill tone={s.status === 'done' ? 'pass' : s.status === 'not_running' ? 'info' : 'warn'}>{storeStatus(s)}</Pill> },
  {
    key: 'read',
    label: 'What was read',
    className: 'quality-small',
    wrap: 2,
    value: (s) => s.message || (s.status === 'done' ? `${plural(s.objects || 0, 'table or collection', 'tables or collections')}, ${plural(s.fields || 0, 'field name')}` : '–'),
  },
];
const BREAKING_COLUMNS = [
  { key: 'service', label: 'Service', className: 'quality-mono', width: 150, value: (f) => f.service || f.extra?.service || '–' },
  { key: 'endpoint', label: 'Endpoint', className: 'quality-mono', width: 220, value: (f) => `${f.extra?.method || ''} ${f.extra?.path || ''}`.trim() },
  { key: 'message', label: 'What changed', wrap: 2, value: (f) => f.message },
  { key: 'change', label: 'Before → now', className: 'quality-mono quality-small', width: 200, value: (f) => (f.extra?.before ? `${f.extra.before} → ${f.extra.after}` : f.extra?.field || '–') },
];
const API_SERVICE_COLUMNS = [
  { key: 'service', label: 'Service', className: 'quality-mono', value: (s) => s.service },
  { key: 'source', label: 'Read from', width: 210, value: (s) => (s.source === 'openapi' ? 'OpenAPI of the running service' : 'The code (routes)') },
  { key: 'endpoints', label: 'Endpoints', width: 100, value: (s) => s.endpoints },
  { key: 'added', label: 'New', width: 70, value: (s) => s.added || 0 },
  { key: 'noAuth', label: 'Without login', width: 120, value: (s) => s.noAuth || 0, render: (s) => (s.noAuth ? <Pill className="is-xs" tone="warn">{s.noAuth}</Pill> : 0) },
  { key: 'breaking', label: 'Breaking', width: 100, value: (s) => s.breaking || 0, render: (s) => (s.breaking ? <Pill className="is-xs" tone="fail">{s.breaking}</Pill> : 0) },
  { key: 'snapshot', label: 'Published contract', width: 160, className: 'quality-small', value: (s) => s.snapshotAt || null, render: (s) => (s.snapshotAt ? relativeTime(s.snapshotAt) : 'None yet') },
];
const overBudget = (b, budgets) => Boolean(budgets.kb && b.jsGzipBytes > budgets.kb * 1024);
function bundleColumns(budgets) {
  return [
    { key: 'service', label: 'Service', className: 'quality-mono', width: 160, value: (b) => b.service },
    { key: 'status', label: 'Status', width: 150, value: (b) => b.message || (overBudget(b, budgets) ? 'warn' : b.status), title: (b) => b.message, render: (b) => (b.message ? <span className="quality-small">{b.message}</span> : <StatusPill status={overBudget(b, budgets) ? 'warn' : b.status} />) },
    { key: 'dir', label: 'Built into', className: 'quality-mono quality-small', value: (b) => b.dir || '–' },
    { key: 'js', label: 'JavaScript (gzip)', width: 170, value: (b) => b.jsGzipBytes, render: (b) => <>{b.jsGzipBytes !== undefined ? kb(b.jsGzipBytes) : '–'}{overBudget(b, budgets) ? <> <Pill className="is-xs" tone="warn">over budget</Pill></> : null}</> },
    {
      // Not cut: the largest files open below the size.
      key: 'total',
      label: 'Everything',
      width: 200,
      clip: false,
      value: (b) => b.totalBytes,
      render: (b) => (
        <>
          {b.totalBytes !== undefined ? kb(b.totalBytes) : '–'}
          {(b.assets || []).length > 0 && (
            <details className="quality-area-assets">
              <summary>Largest files</summary>
              <ul>{b.assets.slice(0, 20).map((a) => <li key={a.file}><span className="quality-mono">{a.file}</span> {kb(a.bytes)} ({kb(a.gzip)} gzip)</li>)}</ul>
            </details>
          )}
        </>
      ),
    },
  ];
}
const slowRoute = (r, budgets) => Boolean(budgets.ms && r.p95 > budgets.ms && r.count >= 3);
function routeColumns(budgets) {
  return [
    { key: 'service', label: 'Service', className: 'quality-mono', width: 150, value: (r) => r.service },
    { key: 'route', label: 'Route', className: 'quality-mono', value: (r) => r.route },
    { key: 'count', label: 'Requests', width: 100, value: (r) => r.count },
    { key: 'p50', label: 'p50', width: 90, value: (r) => r.p50, render: (r) => `${r.p50} ms` },
    { key: 'p95', label: 'p95', width: 130, value: (r) => r.p95, render: (r) => <>{r.p95} ms{slowRoute(r, budgets) ? <> <Pill className="is-xs" tone="warn">slow</Pill></> : null}</> },
    { key: 'errors', label: 'Errors', width: 80, value: (r) => r.errors || 0 },
  ];
}
const pageStatus = (p) => (p.status === 'done' ? 'Checked' : p.status === 'timeout' ? 'Too slow' : 'Not checked');
const PAGE_COLUMNS = [
  { key: 'service', label: 'Service', className: 'quality-mono', width: 160, value: (p) => p.service },
  { key: 'page', label: 'Page', className: 'quality-mono quality-small', value: (p) => p.title || p.url },
  {
    key: 'status',
    label: 'Status',
    width: 220,
    wrap: 2,
    value: pageStatus,
    title: (p) => [pageStatus(p), p.message].filter(Boolean).join(': '),
    render: (p) => (p.status === 'done' ? <Pill tone={p.violations ? 'warn' : 'pass'}>Checked</Pill> : <><Pill tone="warn">{pageStatus(p)}</Pill>{p.message ? <span className="quality-small"> {p.message}</span> : null}</>),
  },
  { key: 'issues', label: 'Issues', width: 80, value: (p) => (p.status === 'done' ? p.violations : null), render: (p) => (p.status === 'done' ? p.violations : '–') },
];

/**
 * The services without a lockfile, when the check has nothing to look at because of it. None named (no service
 * declares dependencies): null, never a "No lockfile" banner (QualityCheck shows "No dependencies to check").
 */
function locklessOf(check) {
  const d = check?.details || {};
  const names = Array.isArray(d.noLockfile) ? d.noLockfile : [];
  const fromFindings = (check?.findings || []).filter((f) => f.rule === 'no-lockfile').map((f) => f.service).filter(Boolean);
  const services = names.length ? names : fromFindings;
  const empty = !d.packages && !d.components;
  return services.length && (check?.unchecked || empty) ? services : null;
}

function NoLockfile({ services, blocks = false, canEdit, onCreateLockfiles, lockfileBusy }) {
  const shown = services.length > 4 ? `${services.slice(0, 4).join(', ')}…` : services.join(', ');
  return (
    <Banner
      actions={canEdit && onCreateLockfiles ? (
        <button className="quality-btn is-primary is-sm" disabled={lockfileBusy} onClick={() => onCreateLockfiles(services)} type="button">
          {lockfileBusy ? 'Creating…' : 'Create lockfiles'}
        </button>
      ) : null}
      className="quality-area-banner"
      icon={<Package size={18} />}
      testId="quality-no-lockfile"
      tone={blocks ? 'fail' : 'warn'}
    >
      <span>
        <strong>{`No lockfile in ${shown}`} — dependencies can&apos;t be checked.</strong>
        {' '}A lockfile lists the exact version of every package. Without it, licences, known vulnerabilities and the SBOM have nothing to look at.
        {' '}Create lockfiles runs each service&apos;s own package manager in your sandbox (npm, pnpm, Yarn, uv, poetry, go, cargo, dotnet, gradle) and saves what it writes.
        {blocks && ' A missing check never counts as passing, so this blocks Publish until each service has one, or it is waived in Settings with a reason.'}
      </span>
    </Banner>
  );
}

NoLockfile.propTypes = { blocks: PropTypes.bool, canEdit: PropTypes.bool, lockfileBusy: PropTypes.bool, onCreateLockfiles: PropTypes.func, services: PropTypes.arrayOf(PropTypes.string).isRequired };

function Readiness({ check, canEdit, onAskAI, onOpenCode }) {
  const items = (check.findings || []).filter((f) => !f.ignored).map((f) => {
    const item = readinessItemOf(f);
    return { ...item, blocks: Boolean(f.extra?.blocks) || Boolean(check.blocks && ['high', 'critical'].includes(f.severity)) };
  });
  if (!items.length) {
    return <p className="quality-banner is-info" data-testid="quality-readiness-ok">Nothing found that would stop the app working outside the preview.</p>;
  }
  return (
    <section aria-label="Production readiness items" className="quality-area" data-testid="quality-readiness">
      <div className="quality-area-head">
        <h2>{plural(items.length, 'thing')} to fix before this works in production</h2>
        {canEdit && onAskAI && <button className="quality-btn is-sm" onClick={() => onAskAI(readinessPrompt(items))} type="button"><Sparkle aria-hidden size={13} />Fix all with AI</button>}
      </div>
      <ul className="quality-area-items">
        {items.map((item) => (
          <li className="quality-card quality-pad quality-area-item" data-testid={`quality-readiness-${item.id}`} key={item.id}>
            <div className="quality-area-item-head">
              <strong className="quality-grow">{item.title}</strong>
              <Pill tone={item.blocks ? 'fail' : 'warn'}>{item.blocks ? 'Blocks Publish' : 'Warns only'}</Pill>
            </div>
            <dl className="quality-area-dl">
              {item.why && <><dt>Why it matters</dt><dd>{item.why}</dd></>}
              <dt>Where</dt>
              <dd>{item.file ? <CodeLink file={item.file} line={item.line} onOpenCode={onOpenCode} /> : (item.service ? <span className="quality-mono">{item.service}</span> : '–')}</dd>
              {item.fix && <><dt>How to fix</dt><dd>{item.fix}</dd></>}
            </dl>
            {canEdit && onAskAI && (
              <div><button className="quality-btn is-sm" onClick={() => onAskAI(readinessPrompt([item]))} type="button"><Sparkle aria-hidden size={13} />Fix with AI</button></div>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}

Readiness.propTypes = { canEdit: PropTypes.bool, check: PropTypes.object.isRequired, onAskAI: PropTypes.func, onOpenCode: PropTypes.func };

function CodeStats({ check, onOpenCode }) {
  const d = check.details || {};
  const totals = d.totals || {};
  const c = d.complexity || {};
  const dup = d.duplication || {};
  const todos = d.todos || {};
  return (
    <section aria-label="Code stats" className="quality-area" data-testid="quality-codestats">
      <p className="quality-muted quality-small">Info only: never blocks Publish.{c.thresholdSet ? '' : ' Set limits in Settings to be warned.'}</p>
      <div className="quality-area-stats">
        <div><strong>{formatCount(totals.code || 0)}</strong><span className="quality-muted quality-small">lines of code</span></div>
        <div><strong>{formatCount(totals.files || 0)}</strong><span className="quality-muted quality-small">files</span></div>
        <div><strong>{c.average ?? '–'}</strong><span className="quality-muted quality-small">average complexity</span></div>
        <div><strong>{typeof dup.percent === 'number' ? `${dup.percent}%` : '–'}</strong><span className="quality-muted quality-small">duplicated</span></div>
        <div><strong>{todos.total || 0}</strong><span className="quality-muted quality-small">TODO / FIXME</span></div>
      </div>
      {(d.languages || []).length > 0 && (
        <div className="quality-card quality-table-box">
          <DataTable
            ariaLabel="Languages"
            className="quality-table"
            columns={LANGUAGE_COLUMNS}
            rowKey="language"
            rowProps={(l) => ({ className: l.other ? 'is-muted' : undefined })}
            rows={d.languages}
            storageKey="flow.quality.languages.hiddenColumns"
          />
        </div>
      )}
      {(d.services || []).length > 0 && (
        <div className="quality-card quality-table-box">
          <DataTable
            ariaLabel="Services"
            className="quality-table"
            columns={SERVICE_STATS_COLUMNS}
            rowKey="service"
            rows={d.services}
            storageKey="flow.quality.serviceStats.hiddenColumns"
          />
        </div>
      )}
      {(c.top || []).length > 0 && (
        <div className="quality-card quality-table-box">
          <DataTable
            ariaLabel="Most complex functions"
            caption={<caption className="quality-area-caption">The most complex functions{c.measured?.length ? <span className="quality-muted quality-small"> · {c.measured.join(', ')}</span> : null}</caption>}
            className="quality-table"
            columns={[
              { key: 'name', label: 'Function', className: 'quality-mono', value: (f) => f.name || '(anonymous)' },
              { key: 'where', label: 'Where', width: 260, value: (f) => where(f.file, f.line), title: (f) => where(f.file, f.line), render: (f) => <CodeLink file={f.file} line={f.line} onOpenCode={onOpenCode} /> },
              { key: 'complexity', label: 'Complexity', width: 150, value: (f) => f.complexity, render: (f) => <>{f.complexity}{c.threshold && f.complexity > c.threshold ? <> <Pill className="is-xs" tone={c.thresholdSet ? 'warn' : 'info'}>over {c.threshold}</Pill></> : null}</> },
            ]}
            rowKey={(f) => `${f.file}:${f.line}:${f.name}`}
            rows={c.top}
          />
        </div>
      )}
      {(d.largest || []).length > 0 && (
        <div className="quality-card quality-table-box">
          <DataTable
            ariaLabel="Largest files"
            caption={<caption className="quality-area-caption">The largest files</caption>}
            className="quality-table"
            columns={[
              { key: 'file', label: 'File', value: (f) => f.file, title: (f) => f.file, render: (f) => <CodeLink file={f.file} onOpenCode={onOpenCode} /> },
              { key: 'lines', label: 'Lines', width: 110, value: (f) => f.lines || 0, render: (f) => formatCount(f.lines || 0) },
              { key: 'language', label: 'Language', width: 150, value: (f) => f.language || '–' },
            ]}
            rowKey="file"
            rows={d.largest}
          />
        </div>
      )}
      {(dup.blocks || []).length > 0 && (
        <div className="quality-card quality-table-box">
          <DataTable
            ariaLabel="Duplicated code"
            caption={<caption className="quality-area-caption">Duplicated code: {dup.percent}% of the lines{dup.duplicatedLines ? ` (${formatCount(dup.duplicatedLines)})` : ''}</caption>}
            className="quality-table"
            columns={[
              { key: 'a', label: 'Here', value: (b) => where(b.a.file, b.a.line), title: (b) => where(b.a.file, b.a.line), render: (b) => <CodeLink file={b.a.file} line={b.a.line} onOpenCode={onOpenCode} /> },
              { key: 'b', label: 'Also here', value: (b) => where(b.b.file, b.b.line), title: (b) => where(b.b.file, b.b.line), render: (b) => <CodeLink file={b.b.file} line={b.b.line} onOpenCode={onOpenCode} /> },
              { key: 'lines', label: 'Lines', width: 90, value: (b) => b.lines },
            ]}
            rowKey={(b) => `${b.a.file}:${b.a.line}-${b.b.file}:${b.b.line}`}
            rows={dup.blocks}
          />
        </div>
      )}
      {todos.total > 0 && (
        <p className="quality-small" data-testid="quality-codestats-todos">
          Notes left in the code: {Object.entries(todos.byKind || {}).filter(([, n]) => n).map(([k, n]) => `${k} ${n}`).join(' · ')}
        </p>
      )}
    </section>
  );
}

CodeStats.propTypes = { check: PropTypes.object.isRequired, onOpenCode: PropTypes.func };

function Privacy({ check }) {
  const d = check.details || {};
  return (
    <section aria-label="Personal data" className="quality-area" data-testid="quality-privacy">
      <p className="quality-banner is-info quality-area-note" data-testid="quality-privacy-note">
        <ShieldCheck aria-hidden size={16} /> <strong>Only names of fields are read, never their values.</strong> Nothing stored in your databases or logs is copied here.
      </p>
      {(d.categories || []).length > 0 && (
        <ul aria-label="Kinds of personal data" className="quality-tags">
          {d.categories.map((c) => <li key={c.category}>{c.label || c.category} <strong>{c.count}</strong></li>)}
        </ul>
      )}
      {(d.services || []).length > 0 && (
        <div className="quality-card quality-table-box">
          <DataTable
            ariaLabel="Personal data per service"
            className="quality-table"
            columns={PRIVACY_COLUMNS}
            rowKey="service"
            rows={d.services}
            storageKey="flow.quality.privacy.hiddenColumns"
          />
        </div>
      )}
      {(d.stores || []).length > 0 && (
        <div className="quality-card quality-table-box">
          <DataTable
            ariaLabel="Datastores"
            className="quality-table"
            columns={STORE_COLUMNS}
            rowKey="store"
            rows={d.stores}
          />
        </div>
      )}
    </section>
  );
}

Privacy.propTypes = { check: PropTypes.object.isRequired };

function ApiContract({ check, onOpenCode }) {
  const d = check.details || {};
  const live = (check.findings || []).filter((f) => !f.ignored);
  const breaking = live.filter((f) => BREAKING.includes(f.rule));
  const noAuth = live.filter((f) => f.rule === 'api-no-auth');
  return (
    <section aria-label="API contract" className="quality-area" data-testid="quality-apicontract">
      {!d.snapshot && <p className="quality-banner is-info">No published contract yet: the first Publish saves one, then each change is compared with it.</p>}
      {breaking.length > 0 && (
        <div className="quality-card quality-table-box" data-testid="quality-api-breaking">
          <DataTable
            ariaLabel="Breaking changes"
            caption={<caption className="quality-area-caption">Breaking changes since the last publish: apps or services that call these may stop working</caption>}
            className="quality-table"
            columns={BREAKING_COLUMNS}
            rowKey="id"
            rows={breaking}
            storageKey="flow.quality.breaking.hiddenColumns"
          />
        </div>
      )}
      {(d.services || []).length > 0 && (
        <div className="quality-card quality-table-box">
          <DataTable
            ariaLabel="API per service"
            className="quality-table"
            columns={API_SERVICE_COLUMNS}
            rowKey="service"
            rows={d.services}
            storageKey="flow.quality.apiServices.hiddenColumns"
          />
        </div>
      )}
      {noAuth.length > 0 && (
        <div className="quality-card quality-pad" data-testid="quality-api-noauth">
          <h3 className="quality-area-h3">Endpoints anyone can call, without logging in</h3>
          <p className="quality-muted quality-small">A security hint: check that each of these is meant to be public. Protect the others with requireUser or requireRole.</p>
          <ul className="quality-area-list">
            {noAuth.map((f) => (
              <li key={f.id}><span className="quality-mono">{f.extra?.method} {f.extra?.path}</span>{f.service ? <span className="quality-muted"> · {f.service}</span> : null}{f.file ? <> · <CodeLink file={f.file} line={f.line} onOpenCode={onOpenCode} /></> : null}</li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}

ApiContract.propTypes = { check: PropTypes.object.isRequired, onOpenCode: PropTypes.func };

function Performance({ check }) {
  const d = check.details || {};
  const budgets = d.budgets || {};
  return (
    <section aria-label="Performance" className="quality-area" data-testid="quality-performance">
      <p className="quality-muted quality-small">Budgets: {budgets.kb ?? 500} KB of JavaScript per web service (gzip) · {budgets.ms ?? 1000} ms for 95% of server requests. Change them in Settings.</p>
      {(d.bundles || []).length > 0 && (
        <div className="quality-card quality-table-box">
          <DataTable
            ariaLabel="Bundle size"
            caption={<caption className="quality-area-caption">How heavy the web pages are</caption>}
            className="quality-table"
            columns={bundleColumns(budgets)}
            rowKey="service"
            rows={d.bundles}
            storageKey="flow.quality.bundles.hiddenColumns"
          />
        </div>
      )}
      {(d.routes || []).length > 0 && (
        <div className="quality-card quality-table-box">
          <DataTable
            ariaLabel="Server response times"
            caption={<caption className="quality-area-caption">How fast the server answers (from Traces, the last hour)</caption>}
            className="quality-table"
            columns={routeColumns(budgets)}
            rowKey={(r) => `${r.service}:${r.route}`}
            rowProps={(r) => ({ className: slowRoute(r, budgets) ? 'is-over' : undefined })}
            rows={d.routes}
            storageKey="flow.quality.routes.hiddenColumns"
          />
        </div>
      )}
      {d.tracesNote && <p className="quality-muted quality-small">{d.tracesNote}</p>}
    </section>
  );
}

Performance.propTypes = { check: PropTypes.object.isRequired };

function Accessibility({ check, canEdit, onAskAI, onRunA11y }) {
  const d = check.details || {};
  const live = (check.findings || []).filter((f) => !f.ignored);
  const impactOf = (f) => f.extra?.impact || ({ critical: 'critical', high: 'serious', medium: 'moderate', low: 'minor' }[f.severity]) || 'moderate';
  return (
    <section aria-label="Accessibility" className="quality-area" data-testid="quality-accessibility">
      <div className="quality-area-head">
        <p className="quality-muted quality-small quality-grow">
          {d.via === 'headless'
            ? 'A browser in your Flow opens each running web page (axe-core, WCAG 2.2 AA), without you having the preview open.'
            : 'It runs in your browser on each running web page (axe-core, WCAG 2.2 AA). Start the previews first.'}
          {d.via && <> <Pill className="is-xs" data-testid="quality-a11y-via" tone="info">{d.via === 'headless' ? 'Checked headless before Publish' : 'Checked in your browser'}</Pill></>}
        </p>
        {canEdit && onRunA11y && <button className="quality-btn is-sm" onClick={onRunA11y} type="button"><Play aria-hidden size={12} weight="fill" />Check the pages</button>}
        {canEdit && onAskAI && live.length > 0 && <button className="quality-btn is-sm" onClick={() => onAskAI(accessibilityPrompt(live))} type="button"><Sparkle aria-hidden size={13} />Fix all with AI</button>}
      </div>
      {(d.pages || []).length > 0 && (
        <div className="quality-card quality-table-box">
          <DataTable
            ariaLabel="Pages checked"
            className="quality-table"
            columns={PAGE_COLUMNS}
            rowKey={(p) => `${p.service}:${p.url}`}
            rows={d.pages}
            storageKey="flow.quality.a11yPages.hiddenColumns"
          />
        </div>
      )}
      {IMPACTS.map((impact) => {
        const group = live.filter((f) => impactOf(f) === impact.id);
        if (!group.length) { return null; }
        return (
          <div className="quality-card quality-pad" data-testid={`quality-a11y-${impact.id}`} key={impact.id}>
            <h3 className="quality-area-h3"><Pill tone={impact.tone}>{impact.label}</Pill> {plural(group.length, 'issue')}</h3>
            <ul className="quality-area-list">
              {group.map((f) => (
                <li className="quality-area-a11y" key={f.id}>
                  <span className="quality-grow">
                    <strong>{f.message}</strong> <span className="quality-muted quality-mono quality-small">{f.rule}</span>
                    <br />
                    {f.extra?.selector && <span className="quality-mono quality-small">{f.extra.selector}</span>}
                    {f.service && <span className="quality-muted quality-small"> · {f.service}</span>}
                  </span>
                  {f.extra?.helpUrl && <a className="quality-btn is-sm is-quiet" href={f.extra.helpUrl} rel="noopener noreferrer" target="_blank">How to fix<ArrowSquareOut aria-hidden size={12} /></a>}
                  {canEdit && onAskAI && <button className="quality-btn is-sm" onClick={() => onAskAI(accessibilityPrompt([f]))} type="button"><Sparkle aria-hidden size={13} />Fix with AI</button>}
                </li>
              ))}
            </ul>
          </div>
        );
      })}
    </section>
  );
}

Accessibility.propTypes = { canEdit: PropTypes.bool, check: PropTypes.object.isRequired, onAskAI: PropTypes.func, onRunA11y: PropTypes.func };

export default function AreaDetails({
  checkId, check, canEdit = false, onAskAI = null, onOpenCode = null, onRunA11y = null, onCreateLockfiles = null, lockfileBusy = false, projectId = null, onChanged = null,
}) {
  if (!check || checkId === 'tests') { return null; }
  const purpose = CHECK_PURPOSE[checkId];
  const ask = canEdit ? onAskAI : null;
  let body = null;
  if (['dependencies', 'vulnerabilities', 'sbom'].includes(checkId)) {
    const services = locklessOf(check);
    const blocks = (check.gaps || []).some((gap) => gap.kind === 'vulnerabilities' && gap.blocking);
    if (services) { body = <NoLockfile blocks={blocks} canEdit={canEdit} lockfileBusy={lockfileBusy} onCreateLockfiles={onCreateLockfiles} services={services} />; }
  } else if (checkId === 'readiness') {
    body = <Readiness canEdit={canEdit} check={check} onAskAI={ask} onOpenCode={onOpenCode} />;
  } else if (checkId === 'codeStats') {
    body = <CodeStats check={check} onOpenCode={onOpenCode} />;
  } else if (checkId === 'privacy') {
    body = <Privacy check={check} />;
  } else if (checkId === 'apiContract') {
    body = <ApiContract check={check} onOpenCode={onOpenCode} />;
  } else if (checkId === 'performance') {
    body = <Performance check={check} />;
  } else if (checkId === 'accessibility') {
    body = <Accessibility canEdit={canEdit} check={check} onAskAI={ask} onRunA11y={onRunA11y} />;
  } else if (checkId === 'codeScanning') {
    body = <CodeScanning check={check} />;
  } else if (checkId === 'lighthouse') {
    body = <Lighthouse check={check} />;
  } else if (checkId === 'external' && projectId) {
    body = <External canEdit={canEdit} check={check} onChanged={onChanged} projectId={projectId} />;
  }
  if (!purpose && !body) { return null; }
  return (
    <div className="quality-area-details" data-testid="quality-area-details">
      {purpose && <p className="quality-small" data-testid="quality-area-purpose"><strong>What this checks:</strong> {purpose}</p>}
      {body}
    </div>
  );
}

AreaDetails.propTypes = {
  canEdit: PropTypes.bool,
  check: PropTypes.object,
  checkId: PropTypes.string.isRequired,
  lockfileBusy: PropTypes.bool,
  onAskAI: PropTypes.func,
  // (services) => void: "Create lockfiles".
  onCreateLockfiles: PropTypes.func,
  onOpenCode: PropTypes.func,
  // () => void: runs the accessibility check in this browser.
  onRunA11y: PropTypes.func,
  // () => void: after an External tools import or removal.
  onChanged: PropTypes.func,
  // External tools' imports and downloads need it.
  projectId: PropTypes.string,
};
